import { z } from 'zod';
import { EventName } from '../ingest/common.ts';
import { PropertyCondition } from './triggers.ts';

/**
 * Funnel and retention reports of a campaign or Canvas (BB-135, AN-003, SEG-010). Both start from a user's **first**
 * start in the period — a receipt of the message (push accepted by the provider, card or in-app impression) or, for a
 * Canvas, the entry; with `groupBy: 'variant'` the control group starts at its assignment (`in_control`) or entry.
 *
 * Funnel: each step must happen after the previous one (in order, each counted once per user) and within `windowDays`
 * of the start; a user leaves the funnel at the first missed step (Braze's rules). Retention: users by the environment
 * day of their start; «returned on day N» means a session (or the event) on calendar day start + N. Test users never
 * count. Results are cached for a few minutes (`generatedAt`).
 */
export const REPORT_STARTS = ['received', 'accepted_by_provider', 'card_impression', 'iam_impression', 'canvas_entered'] as const;
export type ReportStart = (typeof REPORT_STARTS)[number];

/** Message facts a funnel step can be (BB-132 types of the user's own interaction). */
export const FUNNEL_FACT_STEPS = ['delivered', 'push_open_direct', 'push_open_influenced', 'push_button_click', 'card_impression', 'card_click', 'card_dismiss', 'iam_impression', 'iam_click', 'iam_close', 'iam_form_submit'] as const;
export const FUNNEL_MAX_STEPS = 6;
export const FUNNEL_MAX_WINDOW_DAYS = 30;
/** Braze's limit for the entry period: about six months. */
export const REPORT_MAX_PERIOD_DAYS = 180;
export const RETENTION_DEFAULT_DAYS = [1, 3, 7, 14, 30] as const;

const Day = z.iso.date();
const periodDays = (q: { from: string; to: string }) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000;

export const ReportSource = z.union([z.strictObject({ campaignId: z.uuid() }), z.strictObject({ canvasId: z.uuid() })]);
export type ReportSource = z.infer<typeof ReportSource>;

export const FunnelStep = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('fact'), type: z.enum(FUNNEL_FACT_STEPS) }),
  z.strictObject({ kind: z.literal('event'), name: EventName, propertyFilters: z.array(PropertyCondition).max(10).default([]) }),
]);
export type FunnelStep = z.infer<typeof FunnelStep>;

export const ReturnEvent = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('session') }),
  z.strictObject({ kind: z.literal('event'), name: EventName, propertyFilters: z.array(PropertyCondition).max(10).default([]) }),
]);
export type ReturnEvent = z.infer<typeof ReturnEvent>;

/** Rules both reports share: the period, a Canvas-only entry start, variants of a Canvas only by entry. */
const shared = <T extends { source: ReportSource; start: ReportStart; from: string; to: string; groupBy?: 'variant' | undefined }>(q: T, ctx: z.RefinementCtx) => {
  if (q.from > q.to) ctx.addIssue({ code: 'custom', path: ['from'], message: 'from must not be after to' });
  if (periodDays(q) >= REPORT_MAX_PERIOD_DAYS) ctx.addIssue({ code: 'custom', path: ['to'], message: `the period covers at most ${REPORT_MAX_PERIOD_DAYS} days` });
  const canvas = 'canvasId' in q.source;
  if (q.start === 'canvas_entered' && !canvas) ctx.addIssue({ code: 'custom', path: ['start'], message: 'canvas_entered needs a Canvas source' });
  if (canvas && q.groupBy === 'variant' && q.start !== 'canvas_entered') ctx.addIssue({ code: 'custom', path: ['groupBy'], message: 'Canvas variants are known from the entry: use start canvas_entered' });
};

export const FunnelRequest = z
  .strictObject({
    source: ReportSource,
    start: z.enum(REPORT_STARTS).default('received'),
    steps: z.array(FunnelStep).min(1).max(FUNNEL_MAX_STEPS),
    windowDays: z.number().int().min(1).max(FUNNEL_MAX_WINDOW_DAYS).default(3),
    from: Day,
    to: Day,
    groupBy: z.enum(['variant']).optional(),
  })
  .superRefine(shared);
export type FunnelRequest = z.infer<typeof FunnelRequest>;

export const RetentionRequest = z
  .strictObject({
    source: ReportSource,
    start: z.enum(REPORT_STARTS).default('received'),
    returnEvent: ReturnEvent.default({ kind: 'session' }),
    days: z
      .array(z.number().int().min(1).max(90))
      .min(1)
      .max(10)
      .default([...RETENTION_DEFAULT_DAYS])
      .transform((d) => [...new Set(d)].sort((a, b) => a - b)),
    from: Day,
    to: Day,
    groupBy: z.enum(['variant']).optional(),
  })
  .superRefine(shared);
export type RetentionRequest = z.infer<typeof RetentionRequest>;

export interface Localized {
  uk: string;
  en: string;
}

export interface FunnelStepResult {
  /** 0 is the start. */
  index: number;
  kind: 'start' | 'fact' | 'event';
  label: Localized;
  /** Users who reached the step. */
  count: number;
  /** `count / start count`; null when nobody started. */
  rateFromStart: number | null;
  /** `count / previous step count`; null for the start or when nobody reached the previous step. */
  rateFromPrevious: number | null;
  /** Median time from the previous step, in seconds. */
  medianSecondsFromPrevious: number | null;
}

export interface ReportGroup {
  variantApiId: string;
  name: string | null;
  isControl: boolean;
}

export interface FunnelReport {
  source: ReportSource;
  start: ReportStart;
  windowDays: number;
  from: string;
  to: string;
  timezone: string;
  /** All users without control. */
  steps: FunnelStepResult[];
  /** With `groupBy: 'variant'`: each variant and control. */
  groups: Array<ReportGroup & { steps: FunnelStepResult[] }> | null;
  generatedAt: string;
  definition: Localized;
}

export interface RetentionCell {
  day: number;
  /** Users of the cohort(s) whose day N is over (today in the environment is after it). */
  eligible: number;
  /** Null while day N has not ended for any of them. */
  returned: number | null;
  rate: number | null;
}

export interface RetentionRow {
  size: number;
  cells: RetentionCell[];
}

export interface RetentionReport {
  source: ReportSource;
  start: ReportStart;
  returnEvent: ReturnEvent;
  days: number[];
  from: string;
  to: string;
  timezone: string;
  /** Today in the environment: day N of a cohort counts once it is before today. */
  today: string;
  /** One row per start day (without control). */
  cohorts: Array<RetentionRow & { cohortDay: string }>;
  /** All cohorts together: each day over the users whose day N has ended. */
  total: RetentionRow;
  groups: Array<ReportGroup & { total: RetentionRow }> | null;
  generatedAt: string;
  definition: Localized;
}
